import {
  changeMultiFacet,
  setAdditionalQueryParams,
  getVisibleFacets,
  getAdditionalQueryParams
} from "../../state/modules/search";
import { getFloorIdByGender } from "asos-site-chrome-store-presentation";
import { UNISEX_FLOOR } from "../../state/modules/page/constants";
import { getIsSearchPage } from "../../state/modules/page/selectors";
import resetAdditionalQueryParams from "../../state/modules/search/domains/query/helpers/resetAdditionalQueryParams";
import {
  shouldApplyFloorFilters,
  buildFloorFacetSelections,
  getFloorFiltersForNewSearch,
  getResolvedFloor,
  hasFloorFilters
} from "./experimentFloorFilters";

export { getFloorFiltersForNewSearch };

const isAnyFloorDismissed = (state, floorId) => {
  const dismissedFloors = state.search?.dismissedFloors;
  if (!dismissedFloors)
    return { mainFloorDismissed: false, unisexDismissed: false };

  const mainFloorDismissed = dismissedFloors[String(floorId)] === true;
  const unisexDismissed = dismissedFloors[String(UNISEX_FLOOR)] === true;

  return { mainFloorDismissed, unisexDismissed };
};

const hasCorrectFloorFilters = (floorFacet, floorId) => {
  const expectedFloorIds = [String(floorId), String(UNISEX_FLOOR)];
  const selectedFloorIds = floorFacet.facetValues
    .filter(fv => fv.isSelected)
    .map(fv => String(fv.id));

  return (
    expectedFloorIds.every(id => selectedFloorIds.includes(id)) &&
    selectedFloorIds.length === expectedFloorIds.length
  );
};

const applyFloorFiltersWhenNoFacet = (
  dispatch,
  floorId,
  existingRefine,
  dismissedFloors
) => {
  if (existingRefine && hasFloorFilters(existingRefine)) {
    dispatch(setAdditionalQueryParams({ refine: existingRefine }));

    return;
  }

  const floorIds = [String(floorId), String(UNISEX_FLOOR)].filter(
    id => !dismissedFloors?.[id]
  );

  if (floorIds.length === 0) {
    return;
  }

  const params = { refine: `floor:${floorIds.join(",")}` };
  dispatch(setAdditionalQueryParams(params));
};

const applyFloorFiltersWhenFacetExists = (
  dispatch,
  floorFacet,
  floorId,
  dismissedFloors
) => {
  if (hasCorrectFloorFilters(floorFacet, floorId)) {
    return;
  }

  const facetsToSelect = buildFloorFacetSelections(floorFacet, floorId).filter(
    facet => !dismissedFloors?.[String(facet.valueId)]
  );

  if (facetsToSelect.length > 0) {
    dispatch(changeMultiFacet(facetsToSelect));
  }
};

const applyFloorFilters = (dispatch, state, floorId, urlRefine) => {
  const dismissedFloors = state.search?.dismissedFloors || {};
  const additionalQueryParams = getAdditionalQueryParams(state);
  const facets = getVisibleFacets(state);
  const floorFacet = facets.find(f => f.id === "floor");

  if (hasFloorFilters(urlRefine)) {
    dispatch(setAdditionalQueryParams({ refine: urlRefine }));

    if (floorFacet) {
      applyFloorFiltersWhenFacetExists(
        dispatch,
        floorFacet,
        floorId,
        dismissedFloors
      );
    }

    return;
  }

  if (!shouldApplyFloorFilters(state, additionalQueryParams)) {
    return;
  }

  if (!floorFacet) {
    applyFloorFiltersWhenNoFacet(dispatch, floorId, urlRefine, dismissedFloors);
  } else {
    applyFloorFiltersWhenFacetExists(
      dispatch,
      floorFacet,
      floorId,
      dismissedFloors
    );
  }
};

export const setupExperiments = (store, decisions, location) => {
  const { dispatch, getState } = store;
  const state = getState();

  if (!getIsSearchPage(state)) {
    resetAdditionalQueryParams(dispatch, state, "refine");

    return;
  }

  const floor = getResolvedFloor(state);
  const floorId = getFloorIdByGender(floor);

  // Only apply gender filters on men's floor
  if (!floorId || floor !== "men") {
    return;
  }

  const { mainFloorDismissed, unisexDismissed } = isAnyFloorDismissed(
    state,
    floorId
  );
  if (mainFloorDismissed && unisexDismissed) {
    return;
  }

  const urlParams = new URLSearchParams(
    location?.search || window?.location?.search || ""
  );
  const urlRefine = urlParams.get("refine");

  applyFloorFilters(dispatch, state, floorId, urlRefine);
};
